// ============================================================
// TMR Portal Admin — uma linha de contrato, na lista e na aba da empresa
// ============================================================
import { useState } from 'react';
import type { ReactNode } from 'react';
import { IconTile, StatusBadge } from '../../components/primitives';
import { Icons, StrokeIcon } from '../../lib/icons';
import type { ContractRecord } from '../../data/contracts/contract';
import {
  CONTRACT_STATUS_BADGE,
  SIGNATURE_STAGE_LABEL,
  canRequestSignature,
  signatureProviderLabel,
  signatureStage,
} from '../../data/contracts/status';
import { SendContractModal } from './SendContractModal';

/** Props for {@link ContractRow}. */
interface ContractRowProps {
  contract: ContractRecord;
  /** Whether the operator may send for signature (`contracts.manage`). */
  canManage: boolean;
  onOpen: () => void;
  /** Optional lead line, e.g. the Empresa's name on the cross-company list. */
  lead?: ReactNode;
}

/**
 * One contract in a list.
 *
 * A `Pending` contract carries its "enviar para assinatura" action right here, not only inside the creation
 * modal. That is what makes a failed send recoverable rather than lost: the provider is called before
 * anything is written, so a 502 leaves the contract exactly as it was, and the operator meets it again as a
 * row with an obvious next step (09, lacuna L8).
 *
 * The action **opens the confirmation** rather than sending. It used to fire straight from this 34px icon,
 * which meant one click in a table put a document in a client's inbox — with nothing shown first and no way
 * back short of the provider.
 */
export function ContractRow({ contract, canManage, onOpen, lead }: ContractRowProps) {
  const [confirming, setConfirming] = useState(false);
  const stage = signatureStage(contract);
  const sendable = canManage && canRequestSignature(contract.status);

  return (
    <div
      onClick={onOpen}
      className="pa-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        cursor: 'pointer',
        padding: '16px 20px',
        borderBottom: '1px solid var(--border-default)',
      }}
    >
      <IconTile size={38}>
        <StrokeIcon d={Icons.fileText} size={18} />
      </IconTile>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: 14.5 }}>{lead ?? SIGNATURE_STAGE_LABEL[stage]}</div>
        <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)', marginTop: 3 }}>
          <span style={{ fontFamily: 'var(--font-mono)' }}>{contract.id.slice(0, 8)}</span> ·{' '}
          {signatureProviderLabel(contract.signatureProvider)} · criado em{' '}
          {new Date(contract.createdAt).toLocaleDateString('pt-BR')}
          {lead && <> · {SIGNATURE_STAGE_LABEL[stage]}</>}
        </div>
      </div>

      {contract.legacyPartyId === null && (
        <span
          title="Contrato sem vínculo com o produtor do legado: a assinatura não libera o repasse de lá."
          style={{ display: 'flex', color: 'var(--gold-700)' }}
        >
          <StrokeIcon d={Icons.alert} size={16} />
        </span>
      )}

      <StatusBadge status={CONTRACT_STATUS_BADGE[contract.status]} />

      {sendable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setConfirming(true);
          }}
          className="pa-icon-btn"
          title="Enviar para assinatura"
          style={{ width: 34, height: 34 }}
        >
          <StrokeIcon d={Icons.send} size={16} />
        </button>
      )}

      <span style={{ color: 'var(--stone-400)', display: 'flex' }}>
        <StrokeIcon d={Icons.chevronR} size={16} />
      </span>

      {confirming && (
        // Inside the row, so it stops the row's own click from opening the drawer behind it.
        <div onClick={(e) => e.stopPropagation()}>
          <SendContractModal contractId={contract.id} onClose={() => setConfirming(false)} />
        </div>
      )}
    </div>
  );
}
